// Multipart upload client for POST /v1/media/multipart/* (ping-prime §9).

import { log } from '@core/log/log';
import { get, post } from '@core/network/api';

interface MultipartInitResponse {
  media_id: string;
  part_size: number;
  expires_at: number;
}

interface PartUrlResponse {
  url: string;
  expires_in: number;
}

interface CompletedPart {
  part_number: number;
  etag: string;
}

export interface UploadCiphertextResult {
  readonly mediaId: string;
}

/**
 * Optional progress reporter — invoked with the uploaded fraction
 * (0..1) across all parts as the multipart PUTs stream to S3. Driven by
 * `XMLHttpRequest.upload.onprogress` when available; the fetch fallback
 * reports per-part completion only (coarse-grained) so callers still
 * see forward movement on browsers without XHR.
 */
export type UploadProgressFn = (fraction: number) => void;

/**
 * Uploads encrypted ciphertext via the API multipart flow. On failure after
 * `init`, best-effort abort is sent so S3/DB rows do not linger.
 *
 * When `onProgress` is supplied, the uploaded fraction (0..1) is
 * reported live as bytes stream to S3 — used by the chat-room
 * local-echo to drive the per-bubble upload %.
 */
export async function uploadCiphertextMultipart(
  ciphertext: Uint8Array,
  contentTypeHint: string,
  signal?: AbortSignal,
  onProgress?: UploadProgressFn,
): Promise<UploadCiphertextResult> {
  const init = await post<MultipartInitResponse>('/v1/media/multipart/init', {
    size_bytes: ciphertext.byteLength,
    content_type_hint: contentTypeHint,
  });

  const mediaId = init.media_id;
  const partSize = init.part_size;
  const totalBytes = ciphertext.byteLength;
  // Whether XHR (with byte-level upload progress) is available. Falls
  // back to coarse per-part `fetch` progress when not.
  const canXhr =
    onProgress !== undefined && typeof XMLHttpRequest !== 'undefined';
  onProgress?.(0);

  // Pre-compute the part boundaries up front so they can be uploaded in
  // parallel (S3 multipart parts may arrive in any order; `complete` just
  // needs them sorted by part_number — which the indexed array preserves).
  interface PartDef {
    readonly partNumber: number;
    readonly offset: number;
    readonly end: number;
    readonly size: number;
  }
  const partDefs: PartDef[] = [];
  for (let offset = 0, partNumber = 1; offset < ciphertext.length; partNumber += 1) {
    const end = Math.min(offset + partSize, ciphertext.length);
    partDefs.push({ partNumber, offset, end, size: end - offset });
    offset = end;
  }
  const completedParts: CompletedPart[] = new Array(partDefs.length);

  // Smooth aggregate progress across ALL parts: completed-part bytes plus
  // each in-flight part's live `loaded` from its XHR progress event.
  let completedBytes = 0;
  const loadedByPart = new Map<number, number>();
  const report = (): void => {
    if (onProgress === undefined || totalBytes <= 0) return;
    let inFlight = 0;
    for (const v of loadedByPart.values()) inFlight += v;
    onProgress(Math.min(completedBytes + inFlight, totalBytes) / totalBytes);
  };

  // Upload up to this many parts at once. Parallelism is the single
  // biggest upload-speed lever here — a large asset's parts no longer
  // wait for the previous PUT to finish before the next begins.
  const MAX_PARALLEL_PARTS = 4;

  const uploadOnePart = async (pd: PartDef): Promise<void> => {
    if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
    const partOpts = signal !== undefined ? { signal } : {};
    const { url } = await post<PartUrlResponse>(
      '/v1/media/multipart/part-url',
      { media_id: mediaId, part_number: pd.partNumber },
      partOpts,
    );
    const chunkAb = new ArrayBuffer(pd.size);
    new Uint8Array(chunkAb).set(ciphertext.subarray(pd.offset, pd.end));

    let etag: string;
    if (canXhr) {
      etag = await putPartXhr(url, chunkAb, signal, (loaded) => {
        loadedByPart.set(pd.partNumber, loaded);
        report();
      });
    } else {
      const putInit: RequestInit = { method: 'PUT', body: chunkAb };
      if (signal !== undefined) putInit.signal = signal;
      const putRes = await fetch(url, putInit);
      if (!putRes.ok) {
        throw new Error(`S3 part upload failed: ${putRes.status}`);
      }
      const rawEtag = putRes.headers.get('ETag') ?? putRes.headers.get('etag') ?? '';
      etag = rawEtag.replace(/^"|"$/g, '');
    }
    if (etag.length === 0) {
      throw new Error('S3 part upload missing ETag');
    }
    completedParts[pd.partNumber - 1] = { part_number: pd.partNumber, etag };
    loadedByPart.delete(pd.partNumber);
    completedBytes += pd.size;
    report();
  };

  try {
    // Fixed-size worker pool draining a shared cursor over the parts.
    let cursor = 0;
    const worker = async (): Promise<void> => {
      for (;;) {
        const i = cursor;
        cursor += 1;
        const pd = partDefs[i];
        if (pd === undefined) return;
        await uploadOnePart(pd);
      }
    };
    const poolSize = Math.min(MAX_PARALLEL_PARTS, partDefs.length);
    await Promise.all(Array.from({ length: poolSize }, () => worker()));
    onProgress?.(1);

    const completeOpts = signal !== undefined ? { signal } : {};
    await post(
      '/v1/media/multipart/complete',
      { media_id: mediaId, parts: completedParts },
      completeOpts,
    );

    log.debug({
      event: 'media.upload.ok',
      mediaIdPrefix: mediaId.slice(0, 8),
      partCount: completedParts.length,
      sizeBytes: ciphertext.byteLength,
    });

    return { mediaId };
  } catch (err) {
    log.warn({
      event: 'media.upload.failed',
      mediaIdPrefix: mediaId.slice(0, 8),
      err: (err as Error).message,
    });
    try {
      await post('/v1/media/multipart/abort', { media_id: mediaId });
    } catch {
      // best-effort
    }
    throw err;
  }
}

/**
 * PUTs a single ciphertext part to the presigned S3 URL via
 * `XMLHttpRequest` so `upload.onprogress` can report live byte counts.
 * Resolves with the (quote-stripped) ETag. Honors an optional
 * `AbortSignal` by aborting the in-flight request.
 */
function putPartXhr(
  url: string,
  body: ArrayBuffer,
  signal: AbortSignal | undefined,
  onLoaded: (loaded: number) => void,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    const onAbort = (): void => {
      xhr.abort();
    };
    if (signal !== undefined) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    const cleanup = (): void => {
      if (signal !== undefined) signal.removeEventListener('abort', onAbort);
    };

    xhr.upload.onprogress = (e: ProgressEvent): void => {
      if (e.lengthComputable) onLoaded(e.loaded);
    };
    xhr.onload = (): void => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        const rawEtag =
          xhr.getResponseHeader('ETag') ?? xhr.getResponseHeader('etag') ?? '';
        resolve(rawEtag.replace(/^"|"$/g, ''));
      } else {
        reject(new Error(`S3 part upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(new Error('S3 part upload failed: network error'));
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    xhr.send(body);
  });
}

interface MediaFetchResponse {
  download_url: string;
  expires_at: number;
}

export async function fetchMediaDownloadUrl(mediaId: string): Promise<string> {
  const res = await getMediaFetch(mediaId);
  return res.download_url;
}

function getMediaFetch(mediaId: string): Promise<MediaFetchResponse> {
  return get<MediaFetchResponse>(`/v1/media/${encodeURIComponent(mediaId)}`);
}

export async function downloadCiphertextBlob(
  downloadUrl: string,
  signal?: AbortSignal,
  onProgress?: UploadProgressFn,
): Promise<Uint8Array> {
  const init: RequestInit = { method: 'GET' };
  if (signal !== undefined) init.signal = signal;
  const res = await fetch(downloadUrl, init);
  if (!res.ok) {
    throw new Error(`media download failed: ${res.status}`);
  }
  // Stream the body so large blobs (videos) can report download progress.
  // Falls back to a single `arrayBuffer()` read when streaming or the
  // Content-Length header is unavailable.
  const total = Number(res.headers.get('content-length') ?? 0);
  if (onProgress === undefined || res.body === null || !Number.isFinite(total) || total <= 0) {
    const buf = await res.arrayBuffer();
    onProgress?.(1);
    return new Uint8Array(buf);
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value !== undefined) {
      chunks.push(value);
      received += value.byteLength;
      onProgress(Math.min(0.99, received / total));
    }
  }
  const out = new Uint8Array(received);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  onProgress(1);
  return out;
}
